import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { APP_TRANSLATIONS } from '../../core/i18n/app-translations';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import { LanguageService } from '../../core/services/language.service';
import { ContentSkeletonComponent } from '../../core/ui/content-skeleton/content-skeleton.component';
import { downloadBlobFile } from '../../core/utils/browser-file.util';

type PdfImageFormat = 'PNG' | 'JPEG' | 'WEBP';

function sanitizePdfFileNamePart(value: string | null | undefined): string {
  const normalized = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'na';
}

interface YearlyCollectionReportRow {
  monthNo: number;
  monthLabel: string;
  renterName: string;
  paymentDateLabel: string;
  rentAmount: number;
  gasAmount: number;
  electricAmount: number;
  waterAmount: number;
  serviceAmount: number;
  negativeChargeAmount: number;
  advanceAdjustedAmount: number;
  totalAmount: number;
  expenseHeadName: string;
  expenseDateLabel: string;
  expenseAmount: number;
}

interface YearlyExpenseReportRow {
  monthNo: number;
  monthLabel: string;
  renterName: string;
  expenseHeadName: string;
  expenseDateLabel: string;
  expenseAmount: number;
}

@Component({
  selector: 'app-yearly-report-modal',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ContentSkeletonComponent],
  templateUrl: './yearly-report-modal.component.html',
  styleUrl: './yearly-report-modal.component.css'
})
export class YearlyReportModalComponent {
  @Input() visible = false;
  @Input() year = new Date().getFullYear();
  @Input() metric: 'collection' | 'expense' = 'collection';
  @Input() metricLabel = 'Collection';
  @Input() houseLabel = 'N/A';
  @Input() houseNo = 'N/A';
  @Input() houseAddress = 'N/A';
  @Input() flatLabel = 'N/A';
  @Input() floorLabel = 'N/A';
  @Input() negativeChargeTitle = 'Negative Charge';
  @Input() brandName = 'HTALLY';
  @Input() brandLogoUrl: string | null = null;
  @Input() generatedOn = 'N/A';
  @Input() generatedBy = 'N/A';
  @Input() developedBy = 'N/A';
  @Input() loading = false;
  @Input() collectionRows: YearlyCollectionReportRow[] = [];
  @Input() expenseRows: YearlyExpenseReportRow[] = [];
  @Input() collectionTotalAmount = 0;
  @Input() expenseTotalAmount = 0;

  @Output() close = new EventEmitter<void>();
  showRentOnlyInfoModal = false;

  constructor(private readonly language: LanguageService) {}

  get isCollection(): boolean {
    return this.metric === 'collection';
  }

  get collectionTotals(): {
    rent: number;
    gas: number;
    electric: number;
    water: number;
    service: number;
    negative: number;
    advance: number;
    total: number;
  } {
    return this.collectionRows.reduce((acc, row) => {
      acc.rent += Number(row.rentAmount ?? 0);
      acc.gas += Number(row.gasAmount ?? 0);
      acc.electric += Number(row.electricAmount ?? 0);
      acc.water += Number(row.waterAmount ?? 0);
      acc.service += Number(row.serviceAmount ?? 0);
      acc.negative += Number(row.negativeChargeAmount ?? 0);
      acc.advance += Number(row.advanceAdjustedAmount ?? 0);
      acc.total += Number(row.totalAmount ?? 0);
      return acc;
    }, {
      rent: 0,
      gas: 0,
      electric: 0,
      water: 0,
      service: 0,
      negative: 0,
      advance: 0,
      total: 0
    });
  }

  get hasAnyData(): boolean {
    return this.collectionRows.length > 0 || this.expenseRows.length > 0;
  }

  get netCollectionAmount(): number {
    return Number(this.collectionTotalAmount ?? 0) - Number(this.expenseTotalAmount ?? 0);
  }

  get rentOnlyTotalCollectionAmount(): number {
    return Number(this.collectionTotals?.rent ?? 0)
      - Number(this.collectionTotals?.negative ?? 0)
      - Number(this.collectionTotals?.advance ?? 0);
  }

  get rentOnlyNetCollectionAmount(): number {
    return this.rentOnlyTotalCollectionAmount - Number(this.expenseTotalAmount ?? 0);
  }

  get totalNegativeChargeAmount(): number {
    return Number(this.collectionTotals?.negative ?? 0);
  }

  get totalAdvanceAdjustedAmount(): number {
    return Number(this.collectionTotals?.advance ?? 0);
  }

  openRentOnlyInfoModal(): void {
    this.showRentOnlyInfoModal = true;
  }

  closeRentOnlyInfoModal(): void {
    this.showRentOnlyInfoModal = false;
  }

  get negativeChargeHeaderLabel(): string {
    return `${this.negativeChargeTitle ?? ''}`.trim() || this.t('dashboard.legend.negativeCharge', 'Negative Charge');
  }

  formatAmount(value: number | null | undefined): string {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) ? amount.toLocaleString('en-US') : '0';
  }

  formatDeduction(value: number | null | undefined): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      return '0';
    }
    return `(${amount.toLocaleString('en-US')})`;
  }

  displayValue(value: string | null | undefined): string {
    const text = `${value ?? ''}`.trim();
    return text && text !== 'N/A' ? text : this.t('common.na', 'N/A');
  }

  private displayPdfValue(value: string | null | undefined): string {
    const text = `${value ?? ''}`.trim();
    return text && text !== 'N/A' ? text : this.te('common.na', 'N/A');
  }

  private async loadImageForPdf(path: string): Promise<{ dataUrl: string; format: PdfImageFormat } | null> {
    try {
      if (!path) {
        return null;
      }

      if (path.startsWith('data:image/')) {
        return {
          dataUrl: path,
          format: this.detectPdfImageFormat(path.slice(5, path.indexOf(';')))
        };
      }

      const response = await fetch(path);
      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      const format = this.detectPdfImageFormat(blob.type);
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Failed to read logo image.'));
        reader.readAsDataURL(blob);
      });
      return { dataUrl, format };
    } catch {
      return null;
    }
  }

  async downloadPdfReport(): Promise<void> {
    const resolvedBrandName = `${this.brandName ?? ''}`.trim() || 'HTALLY';
    const resolvedBrandLogoUrl = `${this.brandLogoUrl ?? ''}`.trim();
    const fallbackLogoPath = !resolvedBrandLogoUrl && resolvedBrandName === 'HTALLY' ? '/HTallyIcon.png' : '';

    const [{ jsPDF }, logoImage] = await Promise.all([
      import('jspdf'),
      this.loadImageForPdf(resolvedBrandLogoUrl || fallbackLogoPath)
    ]);
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const reportFileName = this.buildPdfReportFileName();
    doc.setProperties({
      title: reportFileName.replace(/\.pdf$/i, ''),
      subject: `${this.metricLabel || (this.isCollection ? 'Collection' : 'Expense')} yearly report`
    });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const left = 34;
    const right = 34;
    const usableW = pageW - left - right;
    let y = 34;

    const colors = {
      titleBg: [224, 246, 250] as [number, number, number],
      titleText: [26, 152, 182] as [number, number, number],
      cardBg: [245, 248, 255] as [number, number, number],
      cardBorder: [220, 229, 245] as [number, number, number],
      label: [109, 123, 161] as [number, number, number],
      value: [31, 38, 65] as [number, number, number],
      tableHeadBg: [234, 242, 255] as [number, number, number],
      tableHeadText: [31, 38, 65] as [number, number, number],
      rowAltBg: [250, 252, 255] as [number, number, number],
      line: [220, 229, 245] as [number, number, number],
      totalBg: [237, 245, 255] as [number, number, number]
    };

    const footerReserve = 42;

    const ensureSpace = (height: number) => {
      if (y + height <= pageH - footerReserve) {
        return;
      }
      doc.addPage();
      y = 34;
    };

    const fitText = (text: string, maxWidth: number): string => {
      const safe = `${text ?? ''}`.trim();
      if (!safe || doc.getTextWidth(safe) <= maxWidth) {
        return safe;
      }
      const ellipsis = '...';
      let output = safe;
      while (output.length > 1 && doc.getTextWidth(`${output}${ellipsis}`) > maxWidth) {
        output = output.slice(0, -1);
      }
      return `${output}${ellipsis}`;
    };

    const wrapHeaderLines = (value: string, maxWidth: number, maxLines = 2): string[] => {
      const text = `${value ?? ''}`.trim();
      if (!text) {
        return [''];
      }
      const wrapped = (doc.splitTextToSize(text, maxWidth) as string[]) || [text];
      if (wrapped.length <= maxLines) {
        return wrapped;
      }
      const lines = wrapped.slice(0, maxLines);
      const lastIndex = maxLines - 1;
      lines[lastIndex] = fitText(lines[lastIndex], maxWidth);
      return lines;
    };

    const drawFlatInfoSection = (top: number): number => {
      const boxTop = top;
      const boxH = 44;
      const contentX = left + 8;
      const contentY = boxTop + 30;
      const contentW = usableW - 16;

      doc.setDrawColor(...colors.cardBorder);
      doc.setFillColor(247, 249, 253);
      doc.roundedRect(left, boxTop, usableW, boxH, 10, 10, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.2);
      doc.setTextColor(...colors.value);
      doc.text(this.te('report.flatInformation', 'Flat Information'), contentX, boxTop + 14);

      const infoChunks = [
        { key: this.te('report.flat', 'Flat'), value: this.displayPdfValue(this.flatLabel) },
        { key: this.te('report.floor', 'Floor'), value: this.displayPdfValue(this.floorLabel) },
        { key: this.te('report.house', 'House'), value: this.displayPdfValue(this.houseLabel) },
        { key: this.te('report.houseNo', 'House No'), value: this.displayPdfValue(this.houseNo) },
        { key: this.te('report.address', 'Address'), value: this.displayPdfValue(this.houseAddress) }
      ];
      const segmentRatios = [0.17, 0.14, 0.2, 0.2, 0.29];
      const segmentWidths = segmentRatios.map((ratio) => contentW * ratio);
      let segmentX = contentX;

      infoChunks.forEach((chunk, index) => {
        const segmentW = segmentWidths[index] ?? (contentW / infoChunks.length);
        const labelText = `${chunk.key}: `;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.8);
        doc.setTextColor(...colors.label);
        doc.text(labelText, segmentX, contentY);

        const labelW = doc.getTextWidth(labelText);
        const valueMaxW = Math.max(12, segmentW - labelW - 2);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.8);
        doc.setTextColor(...colors.value);
        doc.text(fitText(chunk.value, valueMaxW), segmentX + labelW, contentY);

        segmentX += segmentW;
      });

      return boxTop + boxH;
    };

    const fitHeaderText = (text: string, maxWidth: number): string => {
      const safe = `${text ?? ''}`.trim();
      if (!safe || doc.getTextWidth(safe) <= maxWidth) {
        return safe;
      }
      const ellipsis = '...';
      let output = safe;
      while (output.length > 1 && doc.getTextWidth(`${output}${ellipsis}`) > maxWidth) {
        output = output.slice(0, -1);
      }
      return `${output}${ellipsis}`;
    };

    const titleCardHeight = 56;
    doc.setFillColor(...colors.titleBg);
    doc.roundedRect(left, y, usableW, titleCardHeight, 10, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...colors.titleText);
    doc.text(`${this.te('report.yearlyTitle', 'Yearly Collection/Expense Report')} (${this.year})`, left + 14, y + 24);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...colors.label);
    doc.text(fitHeaderText(resolvedBrandName, usableW - 80), left + 14, y + 41);
    if (logoImage) {
      const logoWidth = 30;
      const logoHeight = 30;
      const logoX = left + usableW - logoWidth - 14;
      doc.addImage(logoImage.dataUrl, logoImage.format, logoX, y + 13, logoWidth, logoHeight);
    }
    y += titleCardHeight + 12;

    y = drawFlatInfoSection(y) + 12;

    if (this.collectionRows.length > 0) {
      const rowH = 18;
      const headerH = 34;
      const cols = [
        this.te('report.month', 'Month'),
        this.te('report.renter', 'Renter'),
        this.te('report.payDate', 'Pay Date'),
        this.te('report.rent', 'Rent'),
        this.te('report.gas', 'Gas'),
        this.te('report.elec', 'Elec'),
        this.te('report.water', 'Water'),
        this.te('report.service', 'Service'),
        this.pdfNegativeChargeHeaderLabel,
        this.te('report.adjustFromAdvance', 'Adjust From Advance'),
        this.te('report.total', 'Total')
      ];
      const colW = [
        usableW * 0.11, usableW * 0.13, usableW * 0.1, usableW * 0.075, usableW * 0.06, usableW * 0.07,
        usableW * 0.07, usableW * 0.09, usableW * 0.07, usableW * 0.085, usableW * 0.14
      ];

      ensureSpace(30);
      y += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...colors.value);
      doc.text(this.te('report.collectionDetails', 'Collection Details'), left, y);
      y += 8;

      const drawCollectionHeader = () => {
        doc.setFillColor(...colors.tableHeadBg);
        doc.setDrawColor(...colors.line);
        doc.rect(left, y, usableW, headerH, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.8);
        doc.setTextColor(...colors.tableHeadText);
        let x = left;
        cols.forEach((h, i) => {
          const lines = wrapHeaderLines(h, Math.max(18, colW[i] - 8), 2);
          const tx = x + (colW[i] / 2);
          const baseY = y + 12;
          lines.forEach((line, idx) => {
            doc.text(line, tx, baseY + (idx * 8), { align: 'center' });
          });
          x += colW[i];
        });
        y += headerH;
      };

      drawCollectionHeader();

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.8);
      doc.setTextColor(...colors.value);
      this.collectionRows.forEach((row, idx) => {
        if (y + rowH > pageH - 36) {
          doc.addPage();
          y = 34;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text(this.te('report.collectionDetailsCont', 'Collection Details (cont.)'), left, y);
          y += 8;
          drawCollectionHeader();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.8);
        }
        if (idx % 2 === 1) {
          doc.setFillColor(...colors.rowAltBg);
          doc.rect(left, y, usableW, rowH, 'F');
        }
        const vals = [
          this.pdfMonthLabel(row.monthNo),
          row.renterName,
          row.paymentDateLabel,
          this.formatAmount(row.rentAmount),
          this.formatAmount(row.gasAmount),
          this.formatAmount(row.electricAmount),
          this.formatAmount(row.waterAmount),
          this.formatAmount(row.serviceAmount),
          this.formatDeduction(row.negativeChargeAmount),
          this.formatDeduction(row.advanceAdjustedAmount),
          this.formatAmount(row.totalAmount)
        ];
        let x = left;
        vals.forEach((value, i) => {
          const align: 'left' | 'right' = i < 3 ? 'left' : 'right';
          const tx = align === 'left' ? x + 4 : x + colW[i] - 4;
          doc.text(fitText(`${value ?? ''}`, colW[i] - 8), tx, y + 12, { align });
          x += colW[i];
        });
        doc.setDrawColor(...colors.line);
        doc.line(left, y + rowH, left + usableW, y + rowH);
        y += rowH;
      });

      ensureSpace(rowH + 2);
      doc.setFillColor(...colors.totalBg);
      doc.rect(left, y, usableW, rowH, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const totalVals = [
        this.te('report.finalTotal', 'Final Total'),
        '-',
        '-',
        this.formatAmount(this.collectionTotals.rent),
        this.formatAmount(this.collectionTotals.gas),
        this.formatAmount(this.collectionTotals.electric),
        this.formatAmount(this.collectionTotals.water),
        this.formatAmount(this.collectionTotals.service),
        this.formatDeduction(this.collectionTotals.negative),
        this.formatDeduction(this.collectionTotals.advance),
        this.formatAmount(this.collectionTotals.total)
      ];
      let tx = left;
      totalVals.forEach((value, i) => {
        const align: 'left' | 'right' = i < 3 ? 'left' : 'right';
        const xPos = align === 'left' ? tx + 4 : tx + colW[i] - 4;
        doc.text(fitText(value, colW[i] - 8), xPos, y + 12, { align });
        tx += colW[i];
      });
      y += rowH + 10;
    }

    if (this.expenseRows.length > 0) {
      const rowH = 18;
      const headerH = 28;
      const cols = [
        this.te('report.monthName', 'Month Name'),
        this.te('report.expenseDate', 'Expense Date'),
        this.te('report.expenseHead', 'Expense Head'),
        this.te('report.amount', 'Amount')
      ];
      const colW = [usableW * 0.19, usableW * 0.19, usableW * 0.43, usableW * 0.19];

      ensureSpace(30);
      y += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...colors.value);
      doc.text(this.te('report.expenseDetails', 'Expense Details'), left, y);
      y += 8;

      const drawExpenseHeader = () => {
        doc.setFillColor(...colors.tableHeadBg);
        doc.setDrawColor(...colors.line);
        doc.rect(left, y, usableW, headerH, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        let x = left;
        cols.forEach((h, i) => {
          const lines = wrapHeaderLines(h, Math.max(24, colW[i] - 8), 2);
          const align: 'left' | 'right' = i === 3 ? 'right' : 'left';
          const tx = align === 'left' ? x + 4 : x + colW[i] - 4;
          const baseY = y + 11;
          lines.forEach((line, idx) => {
            doc.text(line, tx, baseY + (idx * 8), { align });
          });
          x += colW[i];
        });
        y += headerH;
      };

      drawExpenseHeader();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.3);
      this.expenseRows.forEach((row, idx) => {
        if (y + rowH > pageH - 36) {
          doc.addPage();
          y = 34;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text(this.te('report.expenseDetailsCont', 'Expense Details (cont.)'), left, y);
          y += 8;
          drawExpenseHeader();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.3);
        }
        if (idx % 2 === 1) {
          doc.setFillColor(...colors.rowAltBg);
          doc.rect(left, y, usableW, rowH, 'F');
        }
        const vals = [row.monthLabel, row.expenseDateLabel, row.expenseHeadName, this.formatAmount(row.expenseAmount)];
        vals[0] = this.pdfMonthLabel(row.monthNo);
        let x = left;
        vals.forEach((value, i) => {
          const align: 'left' | 'right' = i === 3 ? 'right' : 'left';
          const tx = align === 'left' ? x + 4 : x + colW[i] - 4;
          doc.text(fitText(`${value ?? ''}`, colW[i] - 8), tx, y + 12, { align });
          x += colW[i];
        });
        doc.setDrawColor(...colors.line);
        doc.line(left, y + rowH, left + usableW, y + rowH);
        y += rowH;
      });

      ensureSpace(rowH + 2);
      doc.setFillColor(...colors.totalBg);
      doc.rect(left, y, usableW, rowH, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.text(this.te('report.finalTotal', 'Final Total'), left + 4, y + 12);
      doc.text(this.formatAmount(this.expenseTotalAmount), left + usableW - 4, y + 12, { align: 'right' });
      y += rowH + 10;
    }

    ensureSpace(88);
    const gap = 10;
    const cardW = (usableW - gap) / 2;
    const cardH = 84;

    const drawSummaryCard = (
      x: number,
      top: number,
      title: string,
      lines: Array<{ label: string; value: string }>
    ) => {
      doc.setFillColor(...colors.totalBg);
      doc.setDrawColor(...colors.line);
      doc.roundedRect(x, top, cardW, cardH, 8, 8, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.6);
      doc.setTextColor(...colors.value);
      doc.text(title, x + 10, top + 15);

      let lineY = top + 33;
      lines.forEach((line) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.8);
        doc.setTextColor(...colors.label);
        doc.text(`${line.label}:`, x + 10, lineY);
        doc.setTextColor(...colors.value);
        doc.text(line.value, x + cardW - 10, lineY, { align: 'right' });
        lineY += 16;
      });
    };

    drawSummaryCard(left, y, this.te('report.overallTotals', 'Overall Totals'), [
      { label: this.te('report.collection', 'Collection'), value: this.formatAmount(this.collectionTotalAmount) },
      { label: this.te('report.expense', 'Expense'), value: this.formatAmount(this.expenseTotalAmount) },
      { label: this.te('dashboard.netCollection', 'Net Collection'), value: this.formatAmount(this.netCollectionAmount) }
    ]);
    drawSummaryCard(left + cardW + gap, y, this.te('report.rentOnlyTotals', 'Rent Only Totals'), [
      { label: this.te('report.totalFlatRent', 'Total Flat Rent'), value: this.formatAmount(this.rentOnlyTotalCollectionAmount) },
      { label: this.te('report.netRentMinusExpense', 'Net (Rent - Expense)'), value: this.formatAmount(this.rentOnlyNetCollectionAmount) }
    ]);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      const footerY = pageH - 18;
      doc.setDrawColor(...colors.line);
      doc.line(left, footerY - 12, pageW - right, footerY - 12);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...colors.label);
      doc.text(`${this.te('report.generatedOn', 'Generated On')}: ${this.generatedOn}`, left, footerY);
      doc.text(`${this.te('report.generatedBy', 'Generated By')}: ${this.generatedBy}`, left + (usableW * 0.43), footerY);
      doc.text(`${this.te('report.developedBy', 'Developed by')}: ${this.developedBy}`, pageW - right, footerY, { align: 'right' });
    }

    downloadBlobFile(doc.output('blob') as Blob, reportFileName);
  }

  private t(key: string, fallback = ''): string {
    return this.language.translate(key, fallback);
  }

  private te(key: string, fallback = ''): string {
    return APP_TRANSLATIONS.en[key] ?? fallback;
  }

  private detectPdfImageFormat(mimeType: string): PdfImageFormat {
    const normalized = `${mimeType ?? ''}`.trim().toLowerCase();
    if (normalized.includes('png')) {
      return 'PNG';
    }
    if (normalized.includes('webp')) {
      return 'WEBP';
    }
    return 'JPEG';
  }

  private get pdfNegativeChargeHeaderLabel(): string {
    const text = `${this.negativeChargeTitle ?? ''}`.trim();
    const localizedDefault = this.t('dashboard.legend.negativeCharge', 'Negative Charge');
    return !text || text === localizedDefault
      ? this.te('dashboard.legend.negativeCharge', 'Negative Charge')
      : text;
  }

  private pdfMonthLabel(monthNo: number): string {
    const idx = Number(monthNo) - 1;
    if (!Number.isFinite(idx) || idx < 0 || idx >= 12) {
      return this.te('dashboard.unknownMonth', 'Unknown');
    }

    const monthKeys = [
      'monthShort.january',
      'monthShort.february',
      'monthShort.march',
      'monthShort.april',
      'monthShort.may',
      'monthShort.june',
      'monthShort.july',
      'monthShort.august',
      'monthShort.september',
      'monthShort.october',
      'monthShort.november',
      'monthShort.december'
    ] as const;
    const yearShort = `${this.year}`.slice(-2);
    return `${this.te(monthKeys[idx], 'Month')}, ${yearShort}`;
  }

  private buildPdfReportFileName(): string {
    const metricPart = sanitizePdfFileNamePart(this.isCollection ? 'yearly collection report' : 'yearly expense report');
    const yearPart = sanitizePdfFileNamePart(`${this.year}`);
    const housePart = sanitizePdfFileNamePart(this.houseLabel);
    const floorPart = sanitizePdfFileNamePart(this.floorLabel);
    const flatPart = sanitizePdfFileNamePart(this.flatLabel);

    return `${metricPart}-${yearPart}-${housePart}-${floorPart}-${flatPart}.pdf`;
  }
}
